import type { MACDResult } from "../../model/calcMACD";
import type { MACDSettings } from "../../model";
import { COLORS, GRID } from "features/chart";

const TOP = 8;
const BOTTOM = 20;

export function drawMACD(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  result: MACDResult,
  visibleStart: number,
  visibleEnd: number,
  settings: MACDSettings,
): void {
  const { left, right } = GRID;
  const chartW = width - left - right;
  const chartH = height - TOP - BOTTOM;

  ctx.fillStyle = COLORS.bg;
  ctx.fillRect(0, 0, width, height);

  const startIdx = Math.floor(visibleStart);
  const endIdx = Math.ceil(visibleEnd);
  const visibleCount = Math.floor(visibleEnd) - startIdx + 1;
  const cw = chartW / visibleCount;

  let absMax = 0;
  for (const arr of [result.macd, result.signal, result.histogram]) {
    for (let i = startIdx; i <= endIdx; i++) {
      const v = arr[i];
      if (v === null || v === undefined) continue;
      if (Math.abs(v) > absMax) absMax = Math.abs(v);
    }
  }
  if (!absMax) absMax = 1;
  const pad = absMax * 0.1;
  const maxVal = absMax + pad;
  const minVal = -(absMax + pad);
  const range = maxVal - minVal;
  const valToY = (v: number) => TOP + ((maxVal - v) / range) * chartH;
  const zeroY = valToY(0);

  ctx.font = "10px sans-serif";
  const lineEndX = width - right - ctx.measureText(maxVal.toFixed(5)).width - 6;

  ctx.strokeStyle = COLORS.grid;
  ctx.lineWidth = 1;
  for (let i = 0; i <= 2; i++) {
    const y = TOP + (chartH * i) / 2;
    ctx.beginPath();
    ctx.moveTo(left, y);
    ctx.lineTo(lineEndX, y);
    ctx.stroke();
  }

  ctx.strokeStyle = "rgba(255,255,255,0.2)";
  ctx.beginPath();
  ctx.moveTo(left, zeroY);
  ctx.lineTo(lineEndX, zeroY);
  ctx.stroke();

  if (settings.histogramVisible) {
    const barW = Math.max(1, cw * 0.8);
    for (let i = startIdx; i <= endIdx; i++) {
      const v = result.histogram[i];
      if (v === null || v === undefined) continue;
      const x = left + (i - startIdx + 0.5) * cw;
      const y = valToY(v);
      const h = Math.abs(y - zeroY) || 1;
      ctx.fillStyle = v >= 0 ? settings.upColor : settings.downColor;
      ctx.fillRect(x - barW / 2, Math.min(y, zeroY), barW, h);
    }
  }

  const drawLine = (values: (number | null)[], color: string, lw: number) => {
    ctx.strokeStyle = color;
    ctx.lineWidth = lw;
    ctx.beginPath();
    let started = false;
    for (let i = startIdx; i <= endIdx; i++) {
      const v = values[i];
      if (v === null || v === undefined) { started = false; continue; }
      const x = left + (i - startIdx + 0.5) * cw;
      const y = valToY(v);
      if (!started) { ctx.moveTo(x, y); started = true; }
      else ctx.lineTo(x, y);
    }
    ctx.stroke();
  };

  if (settings.macd.visible) drawLine(result.macd, settings.macd.color, settings.macd.width);
  if (settings.signal.visible) drawLine(result.signal, settings.signal.color, settings.signal.width);

  ctx.fillStyle = COLORS.label;
  ctx.textAlign = "right";
  ctx.textBaseline = "middle";
  for (let i = 0; i <= 2; i++) {
    const v = maxVal - (range * i) / 2;
    const y = valToY(v);
    if (y < TOP || y > height - BOTTOM) continue;
    ctx.fillText(v === 0 ? "0" : v.toFixed(5), width - right + 2, y);
  }
}
